import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import {
  MessageSquare,
  Send,
  Search,
  Phone,
  MoreVertical,
  Loader2,
  RefreshCw,
  User,
  Clock,
  CheckCheck,
  AlertCircle,
  Check,
  Plus,
  Smile,
  Download,
  AlertTriangle,
  Timer,
  Trash2,
  Reply,
  X,
  Heart,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  Archive,
  ArchiveRestore,
  FileText,
  Paperclip,
  ChevronLeft
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import type { MetaPageStatus, MetaStatusResponse } from '@easyflow/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@easyflow/ui-frontend/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import NewSmsConversationDialog from '@/components/messaging/NewSmsConversationDialog';
import NewWhatsAppConversationDialog from '@/components/messaging/NewWhatsAppConversationDialog';
import NewTelegramConversationDialog from '@/components/messaging/NewTelegramConversationDialog';
import { useSocket } from '@/contexts/SocketContext';
import WhatsAppTemplateSendModal from '@/components/messaging/WhatsAppTemplateSendModal';
import { getErrorMessage } from '../../lib/utils';


const WhatsAppIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="11" fill="#25D366"/>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="white"/>
  </svg>
);

const SmsIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="11" fill="#3B82F6"/>
    <path d="M7 8.5C7 7.67 7.67 7 8.5 7H15.5C16.33 7 17 7.67 17 8.5V13.5C17 14.33 16.33 15 15.5 15H10L7.5 17V15H8.5H7V8.5Z" fill="white" stroke="white" strokeWidth="0.5"/>
    <circle cx="9.5" cy="11" r="0.75" fill="#3B82F6"/>
    <circle cx="12" cy="11" r="0.75" fill="#3B82F6"/>
    <circle cx="14.5" cy="11" r="0.75" fill="#3B82F6"/>
  </svg>
);

// Task #1417 — supporto canale 'telegram'.
const TelegramIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="11" fill="#229ED9"/>
    <path d="M17.5 7.5L15.4 17.6c-.16.7-.58.87-1.18.55l-3.27-2.41-1.58 1.52c-.18.18-.32.32-.66.32l.23-3.33 6.05-5.47c.26-.23-.06-.36-.4-.13L7.7 13.36 4.51 12.36c-.7-.22-.71-.7.14-1.04L16.6 6.5c.58-.22 1.08.13.9 1z" fill="#fff"/>
  </svg>
);

// Canali Meta (Instagram Direct + Facebook Messenger), reply-only.
const InstagramIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="igGrad" cx="0.3" cy="1" r="1">
        <stop offset="0" stopColor="#FEDA75"/>
        <stop offset="0.35" stopColor="#FA7E1E"/>
        <stop offset="0.6" stopColor="#D62976"/>
        <stop offset="1" stopColor="#962FBF"/>
      </radialGradient>
    </defs>
    <rect x="1" y="1" width="22" height="22" rx="6" fill="url(#igGrad)"/>
    <rect x="6" y="6" width="12" height="12" rx="4" fill="none" stroke="#fff" strokeWidth="1.6"/>
    <circle cx="12" cy="12" r="3" fill="none" stroke="#fff" strokeWidth="1.6"/>
    <circle cx="16.2" cy="7.8" r="1" fill="#fff"/>
  </svg>
);

const MessengerIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="11" fill="#0084FF"/>
    <path d="M12 5.5c-3.7 0-6.5 2.7-6.5 6.3 0 2 .9 3.8 2.4 5v2.4l2.2-1.2c.6.2 1.2.3 1.9.3 3.7 0 6.5-2.7 6.5-6.3S15.7 5.5 12 5.5zm.7 8.5l-1.7-1.8-3.2 1.8 3.5-3.7 1.7 1.8 3.2-1.8-3.5 3.7z" fill="#fff"/>
  </svg>
);

type ChatChannel = 'sms' | 'whatsapp' | 'telegram' | 'instagram' | 'messenger';

const CHANNEL_LABEL: Record<ChatChannel, string> = {
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  instagram: 'Instagram',
  messenger: 'Messenger',
};

// Message tag Meta per l'invio fuori dalla finestra 24h. Instagram supporta SOLO
// HUMAN_AGENT (finestra estesa a 7 giorni); Messenger supporta tutti e quattro i
// tag standard. Vincolo Meta — riflesso 1:1 dal backend.
type MetaMessageTag = 'HUMAN_AGENT' | 'CONFIRMED_EVENT_UPDATE' | 'POST_PURCHASE_UPDATE' | 'ACCOUNT_UPDATE';

const META_ALLOWED_TAGS_BY_CHANNEL: Record<'instagram' | 'messenger', MetaMessageTag[]> = {
  instagram: ['HUMAN_AGENT'],
  messenger: ['HUMAN_AGENT', 'CONFIRMED_EVENT_UPDATE', 'POST_PURCHASE_UPDATE', 'ACCOUNT_UPDATE'],
};

const META_TAG_LABEL: Record<MetaMessageTag, string> = {
  HUMAN_AGENT: 'Agente umano (entro 7 giorni)',
  CONFIRMED_EVENT_UPDATE: 'Aggiornamento evento confermato',
  POST_PURCHASE_UPDATE: 'Aggiornamento post-acquisto',
  ACCOUNT_UPDATE: 'Aggiornamento account',
};

// Allegati Meta (Instagram/Messenger): MIME accettati e limiti (8MB immagini,
// 25MB altri), allineati alla validazione backend (validateMetaMedia).
const META_ATTACH_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,audio/mpeg,audio/mp4,audio/aac,audio/ogg,application/pdf';
const META_IMAGE_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']);
const META_ALLOWED_MIME = new Set([
  ...META_IMAGE_MIME,
  'video/mp4', 'video/quicktime', 'audio/mpeg', 'audio/mp4', 'audio/aac', 'audio/ogg', 'application/pdf',
]);
const META_MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const META_MAX_OTHER_BYTES = 25 * 1024 * 1024;

/** Validazione lato FE allineata ai limiti Meta. Ritorna un messaggio d'errore o null. */
function validateMetaAttachment(file: File): string | null {
  const mime = (file.type || '').toLowerCase();
  if (!META_ALLOWED_MIME.has(mime)) {
    return 'Tipo di file non supportato (immagini, video, audio o PDF)';
  }
  const isImage = META_IMAGE_MIME.has(mime);
  const limit = isImage ? META_MAX_IMAGE_BYTES : META_MAX_OTHER_BYTES;
  if (file.size > limit) {
    return `File troppo grande: il limite per ${isImage ? 'le immagini' : 'questo tipo'} è ${Math.round(limit / (1024 * 1024))}MB`;
  }
  return null;
}

const ChannelIcon = ({ channel, className = "h-4 w-4" }: { channel: ChatChannel; className?: string }) => {
  if (channel === 'whatsapp') return <WhatsAppIcon className={className} />;
  if (channel === 'telegram') return <TelegramIcon className={className} />;
  if (channel === 'sms') return <SmsIcon className={className} />;
  if (channel === 'instagram') return <InstagramIcon className={className} />;
  if (channel === 'messenger') return <MessengerIcon className={className} />;
  console.warn('[MessagingChat] ChannelIcon: canale sconosciuto', channel);
  return (
    <span className={`inline-flex items-center justify-center rounded-full bg-gray-300 text-gray-700 font-bold ${className}`} title={`Canale sconosciuto: ${String(channel)}`}>?</span>
  );
};

interface Conversation {
  id: string;
  contact_number: string;
  last_inbound_at?: string;
  contact_name?: string;
  from_number: string;
  channel: ChatChannel;
  last_message: string;
  last_message_at: string;
  unread_count: number;
  status: 'active' | 'archived';
  can_reply: boolean;
  crm_customer_type?: 'b2c' | 'b2b' | 'lead';
  crm_customer_id?: string;
  crm_person_id?: string | null;
}

const CRM_ENTITY_LABEL: Record<'b2c' | 'b2b' | 'lead', string> = {
  b2c: 'Cliente',
  b2b: 'Azienda',
  lead: 'Lead',
};

interface ConversationGroup {
  key: string;
  primary: Conversation;
  members: Conversation[];
  channels: ChatChannel[];
  totalUnread: number;
}

interface Reaction {
  emoji: string;
  count: number;
  user_ids: string[];
}

interface Message {
  id: string;
  direction: 'inbound' | 'outbound';
  content: string;
  media_url?: string | null;
  media_type?: string | null;
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | 'received';
  created_at: string;
  sent_at?: string | null;
  delivered_at?: string | null;
  read_at?: string | null;
  error_message?: string;
  reply_to_message_id?: string | null;
  reply_to_message?: {
    id: string;
    content: string;
    direction: 'inbound' | 'outbound';
  } | null;
  reactions?: Reaction[];
}

interface ConversationsResponse {
  conversations: Conversation[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface MessagesResponse {
  messages: Message[];
  pagination: {
    page: number;
    limit: number;
  };
}

function MessageMedia({ mediaUrl, mediaType }: { mediaUrl?: string | null; mediaType?: string | null }) {
  // Renderizza solo media già risolti a URL http(s) (presigned R2 o URL
  // pubblico). I file_id Telegram e i riferimenti `r2:` non risolti non sono
  // navigabili: si saltano.
  if (!mediaUrl || !/^https?:\/\//i.test(mediaUrl)) return null;

  const type = (mediaType ?? '').toLowerCase();
  const isImage = type.startsWith('image') || /\.(jpe?g|png|gif|webp|heic|bmp|svg)(\?|$)/i.test(mediaUrl);
  const isVideo = type.startsWith('video') || /\.(mp4|mov|m4v|webm|3gp)(\?|$)/i.test(mediaUrl);
  const isAudio = type.startsWith('audio') || /\.(mp3|m4a|ogg|oga|aac|wav|amr)(\?|$)/i.test(mediaUrl);

  if (isImage) {
    return (
      <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="block mb-1">
        <img
          src={mediaUrl}
          alt="Allegato"
          className="max-w-[260px] max-h-[260px] rounded-lg object-cover"
          loading="lazy"
        />
      </a>
    );
  }
  if (isVideo) {
    return (
      <video src={mediaUrl} controls className="max-w-[260px] max-h-[260px] rounded-lg mb-1" />
    );
  }
  if (isAudio) {
    return <audio src={mediaUrl} controls className="max-w-[260px] mb-1" />;
  }
  return (
    <a
      href={mediaUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 mb-1 underline text-sm"
    >
      <FileText className="h-4 w-4" />
      Apri allegato
    </a>
  );
}

export default function MessagingChat() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Task #1681 — il SW (sw.js) propaga `?channel=sms|whatsapp|telegram` nei
  // deep-link delle notifiche per-canale e nell'azione `reply`. Se la query
  // contiene un canale valido, lo usiamo come stato iniziale del filtro così
  // l'utente atterra direttamente sulla conversazione del canale giusto.
  const [activeChannel, setActiveChannel] = useState<'all' | ChatChannel>(() => {
    if (typeof window === 'undefined') return 'all';
    const qs = new URLSearchParams(window.location.search);
    const raw = (qs.get('channel') || '').toLowerCase();
    return raw === 'sms' || raw === 'whatsapp' || raw === 'telegram' || raw === 'instagram' || raw === 'messenger' ? raw : 'all';
  });
  const [senderFilter, setSenderFilter] = useState<string>('all');
  // Pagine/account Meta del tenant per risolvere i nomi leggibili (page_name,
  // @instagram_username) a partire da `our_phone`/`from_number` = ig:<pageId> o
  // msgr:<pageId>. Fonte: GET /meta/status. Fail-soft: se assente, la select
  // ricade sul page_id grezzo.
  const [metaPages, setMetaPages] = useState<MetaPageStatus[]>([]);
  // Connessioni Meta dell'utente corrente (da /meta/status): nel filtro dei
  // canali Meta mostriamo le Pagine amministrate da almeno una connessione
  // dell'utente (giunzione N:N `connectionIds`, Task #3214), perché
  // preassegnate via la sua OAuth. Fail-soft: se assente, nessun filtro.
  const [metaOwnConnectionIds, setMetaOwnConnectionIds] = useState<string[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  // Canali cold-start: solo questi consentono di aprire una nuova conversazione.
  // Instagram/Messenger sono reply-only (finestra Meta 24h) → nessun dialog.
  const [newConversationChannel, setNewConversationChannel] = useState<'sms' | 'whatsapp' | 'telegram' | null>(null);
  const openNewConversation = () => {
    if (activeChannel === 'sms' || activeChannel === 'whatsapp' || activeChannel === 'telegram') {
      setNewConversationChannel(activeChannel);
    }
  };
  const closeNewConversation = () => setNewConversationChannel(null);
  const handleNewConversationSuccess = async (conversationId: string) => {
    setNewConversationChannel(null);
    const res = await apiClient.get<{ conversations: Conversation[] }>('/messaging/conversations');
    if (res.success && res.data?.conversations) {
      setConversations(res.data.conversations);
      const newConv = res.data.conversations.find(c => c.id === conversationId);
      if (newConv) setSelectedConversation(newConv);
    }
  };
  const [exporting, setExporting] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [showQuickEmoji, setShowQuickEmoji] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [showTemplateShortcut, setShowTemplateShortcut] = useState(false);
  const [templates, setTemplates] = useState<Array<{ id: string; name: string; body_text: string; variables: Record<string, string> | string[] }>>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateVariables, setTemplateVariables] = useState<Record<string, string>>({});
  const [sendingTemplate, setSendingTemplate] = useState(false);
  // Invio Meta fuori finestra 24h: tag selezionato per IG/Messenger.
  const [selectedMetaTag, setSelectedMetaTag] = useState<MetaMessageTag | ''>('');
  // Coda allegati: selezione multipla, ogni file inviato come messaggio Meta a
  // sé (la Send API accetta un solo attachment per messaggio); l'eventuale testo
  // parte come messaggio separato dopo gli allegati.
  const [attachments, setAttachments] = useState<Array<{ id: string; file: File; previewUrl: string | null }>>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const selectedConversationRef = useRef<Conversation | null>(null);
  // Seq-token anti-race: ogni loadConversations incrementa il token; solo
  // l'ultima fetch in volo applica il risultato, evitando che una risposta
  // lenta sovrascriva uno stato più recente (es. update socket per-conv).
  const loadConversationsSeqRef = useRef(0);
  const { isConnected, on, off, socketVersion } = useSocket();

  const loadConversations = useCallback(async (opts?: { background?: boolean }) => {
    const background = opts?.background ?? false;
    const seq = ++loadConversationsSeqRef.current;
    try {
      if (!background) setLoading(true);
      const qs = activeChannel !== 'all' ? `?channel=${activeChannel}` : '';
      const response = await apiClient.get<ConversationsResponse>(`/messaging/conversations${qs}`);
      if (seq !== loadConversationsSeqRef.current) return;
      if (response.success && response.data) {
        setConversations(response.data.conversations);
      }
    } catch (error) {
      if (!background) {
        toast({
          title: 'Errore',
          description: getErrorMessage(error) || 'Impossibile caricare le conversazioni',
          variant: 'destructive'
        });
      }
    } finally {
      if (!background) setLoading(false);
    }
  }, [toast, activeChannel]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (activeChannel === 'all') return;
    if (selectedConversation && selectedConversation.channel !== activeChannel) {
      setSelectedConversation(null);
      setMessages([]);
    }
  }, [activeChannel, selectedConversation]);

  // Il filtro mittente/pagina è specifico per canale: cambiando scheda azzeralo
  // per non lasciare un valore stale (es. un numero WhatsApp mentre si è su IG)
  // che renderebbe la lista vuota.
  useEffect(() => {
    setSenderFilter('all');
  }, [activeChannel]);

  // Carica le pagine Meta una volta: servono a etichettare le conversazioni
  // IG/Messenger col nome leggibile invece del page_id.
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<MetaStatusResponse>('/meta/status')
      .then((res) => {
        if (!cancelled && res.success && res.data) {
          if (res.data.pages) setMetaPages(res.data.pages);
          if (res.data.current_user_id && res.data.connections) {
            setMetaOwnConnectionIds(
              res.data.connections
                .filter((c) => c.connectedByUserId === res.data!.current_user_id)
                .map((c) => c.id)
            );
          }
        }
      })
      .catch(() => { /* fail-soft: la select ricade sul page_id grezzo */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isConnected) {
      console.log('[MessagingChat] Waiting for socket connection...');
      return;
    }

    console.log('[MessagingChat] Using shared socket (version:', socketVersion, ')');

    const handleStatusUpdate = (data: { 
      message_id?: string;
      messageId?: string;
      conversation_id?: string;
      conversationId?: string;
      twilio_sid: string;
      status: string;
      error_code?: string | null;
      error_message?: string | null;
      errorMessage?: string | null;
      updated_at?: string;
      deliveredAt?: string | null;
      readAt?: string | null;
      sentAt?: string | null;
    }) => {
      console.log('[MessagingChat] Status update received:', data);
      const msgId = data.message_id || data.messageId;
      const convId = data.conversation_id || data.conversationId;
      const errMsg = data.error_message || data.errorMessage;
      const updatedAt = data.updated_at || data.deliveredAt || data.readAt || data.sentAt;
      
      if (selectedConversationRef.current?.id === convId) {
        setMessages(prev => {
          const messageExists = prev.some(m => m.id === msgId);
          if (messageExists) {
            return prev.map(m => 
              m.id === msgId ? { 
                ...m, 
                status: data.status as Message['status'],
                error_message: errMsg || m.error_message,
                sent_at: data.status === 'sent' ? (updatedAt || m.sent_at) : m.sent_at,
                delivered_at: data.status === 'delivered' ? (updatedAt || m.delivered_at) : m.delivered_at,
                read_at: data.status === 'read' ? (updatedAt || m.read_at) : m.read_at
              } : m
            );
          }
          return prev;
        });
      }
      // Nessun refetch: lo status update non altera identità/badge della lista.
    };

    const handleNewMessage = (data: { conversationId?: string; conversation_id?: string; unread_count?: number; message: Message }) => {
      console.log('[MessagingChat] New message received:', data);
      const convId = data.conversationId || data.conversation_id;
      if (!convId) return;
      const isOpen = selectedConversationRef.current?.id === convId;
      if (isOpen) {
        setMessages(prev => {
          const incoming = data.message;
          const incomingStatus = (incoming.status
            || (incoming as { twilio_status?: Message['status'] }).twilio_status) as Message['status'] | undefined;
          const existing = prev.find(m => m.id === incoming.id);
          if (!existing) {
            return [...prev, incoming];
          }
          // Già presente (es. il mittente riceve anche l'emit tenant-wide
          // dell'outbound Telegram con status 'sent'): riconcilio lo status
          // dal payload invece di ignorarlo come duplicato.
          if (incomingStatus && incomingStatus !== existing.status) {
            return prev.map(m => (m.id === incoming.id ? { ...m, status: incomingStatus } : m));
          }
          return prev;
        });
      }
      // Aggiornamento per-conv dal payload (no full refetch): se la conv è già
      // in lista la patcho in-place e la risollevo in cima; se è nuova (non
      // presente) faccio un refetch in background per recuperarne l'identità.
      setConversations(prev => {
        const idx = prev.findIndex(c => c.id === convId);
        if (idx === -1) {
          void loadConversations({ background: true });
          return prev;
        }
        const existing = prev[idx]!;
        const patched: Conversation = {
          ...existing,
          last_message: data.message.content ?? existing.last_message,
          last_message_at: data.message.created_at ?? existing.last_message_at,
          last_inbound_at: data.message.direction === 'inbound'
            ? (data.message.created_at ?? existing.last_inbound_at)
            : existing.last_inbound_at,
          unread_count: isOpen ? 0 : (data.unread_count ?? existing.unread_count + 1),
        };
        const next = [...prev];
        next.splice(idx, 1);
        return [patched, ...next];
      });
    };

    const handleConversationRead = (data: { conversation_id: string; unread_count: number }) => {
      console.log('[MessagingChat] Conversation read:', data);
      setConversations(prev => prev.map(conv => 
        conv.id === data.conversation_id 
          ? { ...conv, unread_count: data.unread_count }
          : conv
      ));
    };

    const handleDisconnect = (reason: string) => {
      console.log('[MessagingChat] Socket disconnected:', reason);
    };

    const handleConnectError = (error: Error) => {
      console.error('[MessagingChat] Socket connect_error:', error.message);
    };

    on('messaging:status_update', handleStatusUpdate);
    on('messaging:new_message', handleNewMessage);
    on('messaging:conversation_read', handleConversationRead);
    on('disconnect', handleDisconnect);
    on('connect_error', handleConnectError);

    return () => {
      off('messaging:status_update', handleStatusUpdate);
      off('messaging:new_message', handleNewMessage);
      off('messaging:conversation_read', handleConversationRead);
      off('disconnect', handleDisconnect);
      off('connect_error', handleConnectError);
    };
  }, [isConnected, socketVersion, on, off, loadConversations]);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  const loadMessages = useCallback(async (conversationId: string) => {
    try {
      const response = await apiClient.get<MessagesResponse>(`/messaging/conversations/${conversationId}/messages`);
      if (response.success && response.data) {
        setMessages(response.data.messages);
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile caricare i messaggi',
        variant: 'destructive'
      });
    }
  }, [toast]);

  const resyncAfterReconnect = useCallback(async () => {
    await loadConversations({ background: true });
    const open = selectedConversationRef.current;
    if (open) {
      await loadMessages(open.id);
    }
  }, [loadConversations, loadMessages]);

  const resyncAfterReconnectRef = useRef(resyncAfterReconnect);
  useEffect(() => {
    resyncAfterReconnectRef.current = resyncAfterReconnect;
  }, [resyncAfterReconnect]);

  const didInitialSyncRef = useRef(false);
  useEffect(() => {
    if (!isConnected) return;
    if (!didInitialSyncRef.current) {
      didInitialSyncRef.current = true;
      return;
    }
    console.log('[MessagingChat] Socket (re)connected (version:', socketVersion, ') — resyncing conversations');
    void resyncAfterReconnectRef.current();
  }, [isConnected, socketVersion]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
      if (selectedConversation.unread_count > 0) {
        setConversations(prev => prev.map(conv => 
          conv.id === selectedConversation.id 
            ? { ...conv, unread_count: 0 }
            : conv
        ));
      }
    }
  }, [selectedConversation, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const clearAttachments = useCallback(() => {
    setAttachments(prev => {
      prev.forEach(a => { if (a.previewUrl) URL.revokeObjectURL(a.previewUrl); });
      return [];
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const removeAttachment = useCallback((attachmentId: string) => {
    setAttachments(prev => {
      const target = prev.find(a => a.id === attachmentId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(a => a.id !== attachmentId);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    const valid: Array<{ id: string; file: File; previewUrl: string | null }> = [];
    const errors: string[] = [];
    for (const file of files) {
      const error = validateMetaAttachment(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
        continue;
      }
      valid.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      });
    }
    if (errors.length > 0) {
      toast({ title: 'Alcuni allegati non sono validi', description: errors.join('\n'), variant: 'destructive' });
    }
    if (valid.length > 0) {
      setAttachments(prev => [...prev, ...valid]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Carica l'allegato su R2 via backend e ritorna il riferimento `r2:<key>`
  // insieme al media_type derivato dal MIME reale (per render/persistenza).
  const uploadAttachment = async (file: File): Promise<{ url: string; mediaType: string | null }> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiClient.post<{ media_url: string; media_type?: string | null }>('/messaging/media/upload', formData);
    if (!res.success || !res.data?.media_url) {
      throw new Error(res.message || 'Upload allegato fallito');
    }
    return { url: res.data.media_url, mediaType: res.data.media_type ?? null };
  };

  // Normalizza un messaggio inviato dal backend: stato coerente + eventuale
  // anteprima del messaggio a cui si risponde.
  const normalizeSentMessage = (sent: Message, replyTo: Message | null): Message => ({
    ...sent,
    status: (sent.status || (sent as { twilio_status?: Message['status'] }).twilio_status || 'sent') as Message['status'],
    reply_to_message: replyTo
      ? { id: replyTo.id, content: replyTo.content, direction: replyTo.direction }
      : (sent.reply_to_message ?? null),
  });

  // Invio orchestrato: ogni allegato è un messaggio a sé (Meta Send API accetta
  // un solo attachment per messaggio), poi l'eventuale testo come messaggio
  // separato dopo gli allegati. La risposta (reply_to) è applicata solo al primo
  // messaggio della sequenza.
  const sendQueuedMessages = async (opts?: { replyTo?: Message | null }) => {
    if ((!newMessage.trim() && attachments.length === 0) || !selectedConversation) return;

    const convId = selectedConversation.id;
    const replyTo = opts?.replyTo ?? null;
    const queued = attachments;
    const text = newMessage.trim();

    setSending(true);
    try {
      const sentMessages: Message[] = [];
      let isFirst = true;

      for (const att of queued) {
        let mediaUrl: string;
        let mediaType: string | null = null;
        setUploadingAttachment(true);
        try {
          const uploaded = await uploadAttachment(att.file);
          mediaUrl = uploaded.url;
          mediaType = uploaded.mediaType;
        } finally {
          setUploadingAttachment(false);
        }
        const response = await apiClient.post<Message>(`/messaging/conversations/${convId}/messages`, {
          content: '',
          media_url: mediaUrl,
          ...(mediaType ? { media_type: mediaType } : {}),
          ...(isFirst && replyTo ? { reply_to_message_id: replyTo.id } : {}),
        });
        if (!response.success || !response.data) {
          throw new Error(response.error || response.message || 'Invio allegato fallito');
        }
        sentMessages.push(normalizeSentMessage(response.data, isFirst ? replyTo : null));
        isFirst = false;
      }

      if (text) {
        const response = await apiClient.post<Message>(`/messaging/conversations/${convId}/messages`, {
          content: newMessage,
          ...(isFirst && replyTo ? { reply_to_message_id: replyTo.id } : {}),
        });
        if (!response.success || !response.data) {
          throw new Error(response.error || response.message || 'Invio messaggio fallito');
        }
        sentMessages.push(normalizeSentMessage(response.data, isFirst ? replyTo : null));
        isFirst = false;
      }

      if (sentMessages.length > 0) {
        setMessages(prev => [...prev, ...sentMessages]);
        setNewMessage('');
        setReplyToMessage(null);
        clearAttachments();
      }
    } catch (error) {
      toast({
        title: 'Errore invio',
        description: getErrorMessage(error) || 'Impossibile inviare il messaggio',
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  const sendMessage = () => sendQueuedMessages();

  const loadTemplates = async () => {
    try {
      const res = await apiClient.get<{ templates: Array<{ id: string; friendly_name: string; body_text: string; variables: string[] }> }>('/messaging/templates?status=approved');
      if (res.success && res.data?.templates) {
        setTemplates(res.data.templates.map(t => ({ ...t, name: t.friendly_name })));
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  // Canali con finestra 24h reply-only: WhatsApp + Meta (Instagram/Messenger).
  // WhatsApp riapre con i template approvati; Meta no (solo attesa di un nuovo
  // inbound del contatto).
  const is24hWindowExpired = useCallback((conversation: Conversation | null): boolean => {
    if (!conversation) return false;
    const windowed = conversation.channel === 'whatsapp' || conversation.channel === 'instagram' || conversation.channel === 'messenger';
    if (!windowed) return false;
    if (!conversation.last_inbound_at) return true;
    const lastInbound = new Date(conversation.last_inbound_at);
    const now = new Date();
    const diff = now.getTime() - lastInbound.getTime();
    return diff > 24 * 60 * 60 * 1000;
  }, []);

  useEffect(() => {
    if (selectedConversation?.channel === 'whatsapp' && is24hWindowExpired(selectedConversation)) {
      loadTemplates();
    }
  }, [selectedConversation, is24hWindowExpired]);

  // Cambio conversazione: azzera il tag Meta selezionato (evita di trascinare un
  // tag scelto per un'altra chat, magari non ammesso sul nuovo canale).
  useEffect(() => {
    setSelectedMetaTag('');
  }, [selectedConversation?.id]);

  useEffect(() => {
    if (selectedTemplateId && selectedConversation) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template) {
        const numericVars = template.body_text?.match(/\{\{(\d+)\}\}/g) || [];
        const vars: Record<string, string> = {};
        numericVars.forEach(v => {
          const num = v.replace(/[{}]/g, '');
          vars[num] = '';
        });
        setTemplateVariables(vars);

        const variableMapping = template.variables && !Array.isArray(template.variables) ? template.variables : null;
        if (variableMapping && Object.keys(variableMapping).length > 0 && selectedConversation.contact_number) {
          apiClient.get<{ variables: Record<string, string> }>(`/messaging/resolve-variables?phone=${encodeURIComponent(selectedConversation.contact_number)}`)
            .then(res => {
              if (res.success && res.data?.variables) {
                const resolved: Record<string, string> = {};
                Object.entries(variableMapping).forEach(([num, varKey]) => {
                  if (res.data!.variables[varKey]!) {
                    resolved[num] = res.data!.variables[varKey]!;
                  }
                });
                setTemplateVariables(prev => ({ ...prev, ...resolved }));
              }
            })
            .catch(err => console.error('Failed to resolve template variables:', err));
        }
      }
    } else if (!selectedTemplateId) {
      setTemplateVariables({});
    }
  }, [selectedTemplateId, templates, selectedConversation]);

  const sendTemplateMessage = async () => {
    if (!selectedTemplateId || !selectedConversation) return;
    
    setSendingTemplate(true);
    try {
      const res = await apiClient.post<{ message_sid: string }>(`/messaging/conversations/${selectedConversation.id}/send-template`, {
        template_id: selectedTemplateId,
        template_variables: templateVariables
      });
      
      if (res.success) {
        toast({ title: 'Template inviato con successo' });
        setSelectedTemplateId('');
        setTemplateVariables({});
        loadMessages(selectedConversation.id);
      } else {
        toast({ title: 'Errore', description: res.error || 'Errore invio template', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setSendingTemplate(false);
    }
  };

  // Lookup pagina Meta per page_id (nudo, senza prefisso canale).
  const metaPageByPageId = useMemo(() => {
    const m = new Map<string, MetaPageStatus>();
    for (const p of metaPages) m.set(p.pageId, p);
    return m;
  }, [metaPages]);

  // Etichetta leggibile di un `from_number`/`our_phone`. Per IG/Messenger
  // (prefisso ig:/msgr:) risolve il nome pagina o @username; per gli altri
  // canali resta il numero così com'è. Canale inferito dal prefisso → vale
  // anche nella scheda "Tutti" dove i mittenti sono misti.
  const senderLabel = useCallback((value: string): string => {
    if (value.startsWith('ig:') || value.startsWith('msgr:')) {
      const isInstagram = value.startsWith('ig:');
      const pageId = value.replace(/^(ig:|msgr:)/, '');
      const page = metaPageByPageId.get(pageId);
      if (isInstagram && page?.instagramUsername) return `@${page.instagramUsername}`;
      if (page?.pageName) return page.pageName;
      return pageId;
    }
    return value;
  }, [metaPageByPageId]);

  const isMetaChannel = activeChannel === 'instagram' || activeChannel === 'messenger';
  const senderFilterAllLabel = isMetaChannel ? 'Tutte le pagine' : 'Tutti i numeri';

  // Mittenti disponibili per il filtro, ristretti al canale attivo.
  // - Canali telefonici: derivati dai numeri presenti nelle conversazioni.
  // - Canali Meta (IG/Messenger): popolati dalle Pagine integrate legate
  //   all'utente corrente (preassegnate via la sua OAuth), così appaiono anche
  //   senza conversazioni — parità col filtro numeri. Le identità ricavate
  //   soltanto dallo storico non rientrano nel filtro operativo.
  const availableSenders = useMemo<string[]>(() => {
    const fromConversations = conversations
      .filter(c => activeChannel === 'all' || c.channel === activeChannel)
      .map(c => c.from_number)
      .filter(Boolean);

    if (!isMetaChannel) {
      return Array.from(new Set(fromConversations));
    }

    const prefix = activeChannel === 'instagram' ? 'ig:' : 'msgr:';
    // Strettamente user-scoped (fail-closed): mostriamo SOLO le Pagine
    // amministrate da almeno una connessione OAuth Meta dell'utente corrente.
    // Se lo status non è ancora caricato / in errore non preassegniamo nulla,
    // così non compaiono mai Pagine di altri utenti.
    const fromPages = metaPages
      .filter(p => p.removedAt === null)
      .filter(p => p.connectionIds.some(id => metaOwnConnectionIds.includes(id)))
      .filter(p => activeChannel === 'instagram'
        ? Boolean(p.instagramBusinessAccountId || p.instagramUsername)
        : true)
      .map(p => `${prefix}${p.pageId}`);

    return Array.from(new Set(fromPages));
  }, [conversations, activeChannel, isMetaChannel, metaPages, metaOwnConnectionIds]);

  // Invio Meta fuori finestra 24h: usa il message tag selezionato (IG/Messenger).
  // Il backend valida il tag contro quelli ammessi per il canale e rifiuta i
  // bypass silenziosi (nessun invio fuori finestra senza tag valido).
  const sendMetaTagMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !selectedMetaTag) return;

    setSending(true);
    try {
      const response = await apiClient.post<Message>(`/messaging/conversations/${selectedConversation.id}/messages`, {
        content: newMessage,
        message_tag: selectedMetaTag,
      });

      if (response.success && response.data) {
        const sent = response.data;
        const normalized: Message = {
          ...sent,
          status: (sent.status || (sent as { twilio_status?: Message['status'] }).twilio_status || 'sent') as Message['status'],
        };
        setMessages(prev => [...prev, normalized]);
        setNewMessage('');
      } else {
        toast({ title: 'Errore invio', description: response.error || 'Impossibile inviare il messaggio', variant: 'destructive' });
      }
    } catch (error) {
      toast({
        title: 'Errore invio',
        description: getErrorMessage(error) || 'Impossibile inviare il messaggio',
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = !searchQuery || 
      conv.contact_number.includes(searchQuery) || 
      conv.contact_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesChannel = activeChannel === 'all' || conv.channel === activeChannel;
    const matchesSender = senderFilter === 'all' || conv.from_number === senderFilter;
    const matchesArchived = showArchived ? true : conv.status !== 'archived';
    const matchesUnread = !showUnreadOnly || conv.unread_count > 0;
    return matchesSearch && matchesChannel && matchesSender && matchesArchived && matchesUnread;
  });

  // Aggregazione per persona (solo tab "Tutti"): più conversazioni della stessa
  // persona CRM (crm_person_id) — tipicamente su canali diversi — collassano in
  // una riga unica. La primaria è la conv col messaggio più recente; il badge
  // non-letti è la somma del gruppo e i micro-badge mostrano i canali presenti.
  const conversationGroups = (() => {
    const groups: ConversationGroup[] = [];
    const byPerson = new Map<string, number>();
    for (const conv of filteredConversations) {
      const canGroup = activeChannel === 'all' && !!conv.crm_person_id;
      if (canGroup) {
        const pid = conv.crm_person_id as string;
        const existingIdx = byPerson.get(pid);
        if (existingIdx !== undefined) {
          const g = groups[existingIdx]!;
          g.members.push(conv);
          if (!g.channels.includes(conv.channel)) g.channels.push(conv.channel);
          g.totalUnread += conv.unread_count;
          const convTs = new Date(conv.last_message_at).getTime();
          const primaryTs = new Date(g.primary.last_message_at).getTime();
          if (convTs > primaryTs) g.primary = conv;
          continue;
        }
        byPerson.set(pid, groups.length);
      }
      groups.push({
        key: canGroup ? `person:${conv.crm_person_id}` : conv.id,
        primary: conv,
        members: [conv],
        channels: [conv.channel],
        totalUnread: conv.unread_count,
      });
    }
    return groups;
  })();

  const getRelativeTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const diffMs = Date.now() - date.getTime();
    if (diffMs < 60 * 1000) return 'ora';
    if (diffMs < 7 * 24 * 60 * 60 * 1000) {
      return formatDistanceToNow(date, { locale: it, addSuffix: true });
    }
    return format(date, 'dd/MM/yyyy', { locale: it });
  };

  const getDateLabel = (dateStr: string): string => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const sixDaysAgo = new Date(today);
    sixDaysAgo.setDate(sixDaysAgo.getDate() - 6);
    const toDay = (d: Date) => d.toISOString().split('T')[0];
    if (toDay(date) === toDay(today)) return 'Oggi';
    if (toDay(date) === toDay(yesterday)) return 'Ieri';
    if (date >= sixDaysAgo) return format(date, 'EEEE d MMMM', { locale: it });
    return format(date, 'd MMMM yyyy', { locale: it });
  };

  const getStatusIcon = (status: Message['status'], direction?: 'inbound' | 'outbound') => {
    // For inbound messages, no status icon needed (we received it successfully)
    if (direction === 'inbound') {
      return null;
    }
    
    switch (status) {
      case 'read':
        return <span title="Letto"><CheckCheck className="h-4 w-4 text-sky-300" /></span>;
      case 'delivered':
        return <span title="Consegnato"><CheckCheck className="h-4 w-4 text-white/90" /></span>;
      case 'sent':
        return <span title="Inviato"><Check className="h-4 w-4 text-white/70" /></span>;
      case 'failed':
        return <span title="Fallito"><AlertCircle className="h-4 w-4 text-red-300" /></span>;
      case 'pending':
        return <span title="In attesa"><Clock className="h-4 w-4 text-white/60" /></span>;
      case 'received':
        return null; // No icon needed for received messages
      default:
        return <Clock className="h-4 w-4 text-white/60" />;
    }
  };

  const handleEmojiSelect = (emoji: { native: string }) => {
    setNewMessage(prev => prev + emoji.native);
    setShowEmojiPicker(false);
  };

  const handleExportGDPR = async () => {
    if (!selectedConversation) return;
    setExporting(true);
    try {
      const res = await apiClient.get<{ export: object }>(`/messaging/gdpr/export/${selectedConversation.contact_number}`);
      if (res.success && res.data) {
        const blob = new Blob([JSON.stringify(res.data.export, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gdpr-export-${selectedConversation.contact_number}-${format(new Date(), 'yyyy-MM-dd')}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast({ title: 'Export GDPR completato' });
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation) return;
    
    const conferma = window.confirm(`Sei sicuro di voler eliminare questa conversazione con ${selectedConversation.contact_name || selectedConversation.contact_number}?\n\nTutti i messaggi verranno eliminati definitivamente.`);
    
    if (!conferma) return;
    
    try {
      const res = await apiClient.delete(`/messaging/conversations/${selectedConversation.id}`);
      if (res.success) {
        toast({ title: 'Conversazione eliminata' });
        setSelectedConversation(null);
        setMessages([]);
        loadConversations();
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const handleArchiveConversation = async () => {
    if (!selectedConversation) return;
    
    try {
      const res = await apiClient.patch(`/messaging/conversations/${selectedConversation.id}/archive`);
      if (res.success) {
        toast({ title: 'Conversazione archiviata' });
        setSelectedConversation(null);
        setMessages([]);
        loadConversations();
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const handleUnarchiveConversation = async () => {
    if (!selectedConversation) return;
    
    try {
      const res = await apiClient.patch(`/messaging/conversations/${selectedConversation.id}/unarchive`);
      if (res.success) {
        toast({ title: 'Conversazione riattivata' });
        loadConversations();
        setSelectedConversation(prev => prev ? { ...prev, status: 'active' } : null);
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!selectedConversation) return;
    
    const confirmed = window.confirm('Sei sicuro di voler eliminare questo messaggio? Questa azione non può essere annullata.');
    if (!confirmed) return;
    
    try {
      const res = await apiClient.delete(`/messaging/messages/${messageId}`);
      if (res.success) {
        setMessages(prev => prev.filter(m => m.id !== messageId));
        loadConversations();
        toast({ title: 'Messaggio eliminato' });
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const handleRetryMessage = async (messageId: string) => {
    if (!selectedConversation) return;
    
    try {
      const res = await apiClient.post<Message>(`/messaging/messages/${messageId}/retry`);
      if (res.success && res.data) {
        setMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, status: 'pending', error_message: undefined } : m
        ));
        toast({ title: 'Messaggio rimesso in coda' });
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const handleReplyToMessage = (message: Message) => {
    setReplyToMessage(message);
  };

  const handleQuickReaction = async (messageId: string, emoji: string) => {
    setShowQuickEmoji(null);
    try {
      const response = await apiClient.post<{ reactions: Reaction[] }>(`/messaging/messages/${messageId}/reactions`, { emoji });
      if (response.success && response.data) {
        setMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, reactions: response.data!.reactions! } : m
        ));
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile aggiungere reazione',
        variant: 'destructive'
      });
    }
  };

  const handleRemoveReaction = async (messageId: string, emoji: string) => {
    try {
      const response = await apiClient.delete<{ reactions: Reaction[] }>(`/messaging/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`);
      if (response.success && response.data) {
        setMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, reactions: response.data!.reactions! } : m
        ));
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile rimuovere reazione',
        variant: 'destructive'
      });
    }
  };

  const sendMessageWithReply = () => sendQueuedMessages({ replyTo: replyToMessage });

  const handleSelectConversation = (conv: Conversation) => {
    setSelectedConversation(conv);
    setMobileShowDetail(true);
  };

  const handleMobileBack = () => {
    setMobileShowDetail(false);
  };

  return (
    <div className="h-[calc(100vh-4rem-2cm)] flex">
      <div className={`w-full md:w-96 border-r bg-background flex flex-col ${mobileShowDetail ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-lg">Conversazioni</h2>
            <div className="flex items-center gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={activeChannel === 'all' ? 0 : -1}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={openNewConversation}
                        disabled={activeChannel === 'all'}
                        aria-label="Nuova conversazione"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {activeChannel === 'sms' || activeChannel === 'whatsapp' || activeChannel === 'telegram'
                      ? `Nuova conversazione ${CHANNEL_LABEL[activeChannel]}`
                      : 'Seleziona prima un canale (SMS, WhatsApp o Telegram) dalle schede sotto'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button variant="ghost" size="sm" onClick={() => loadConversations()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca contatto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          {(isMetaChannel ? availableSenders.length >= 1 : availableSenders.length > 1) && (
            <select
              value={senderFilter}
              onChange={(e) => setSenderFilter(e.target.value)}
              className="h-9 w-full px-3 text-sm border rounded-md bg-background"
              title={isMetaChannel ? 'Filtra per pagina/account' : 'Filtra per numero mittente'}
            >
              <option value="all">{senderFilterAllLabel}</option>
              {availableSenders.map(sender => (
                <option key={sender} value={sender}>{senderLabel(sender)}</option>
              ))}
            </select>
          )}
          <Tabs value={activeChannel} onValueChange={(v) => setActiveChannel(v as 'all' | ChatChannel)} className="w-full">
            <TabsList className="w-full">
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="all" className="flex-1" aria-label="Tutti i canali">Tutti</TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent>Tutti i canali</TooltipContent>
                </Tooltip>
                {(['sms', 'whatsapp', 'telegram', 'instagram', 'messenger'] as const).map((ch) => (
                  <Tooltip key={ch}>
                    <TooltipTrigger asChild>
                      <TabsTrigger value={ch} className="flex-1" aria-label={CHANNEL_LABEL[ch]}>
                        <ChannelIcon channel={ch} className="h-4 w-4" />
                      </TabsTrigger>
                    </TooltipTrigger>
                    <TooltipContent>{CHANNEL_LABEL[ch]}</TooltipContent>
                  </Tooltip>
                ))}
              </TooltipProvider>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <Button 
              variant={showUnreadOnly ? "secondary" : "ghost"} 
              size="sm" 
              className="text-xs flex-1"
              onClick={() => setShowUnreadOnly(!showUnreadOnly)}
            >
              <MessageSquare className="h-3 w-3 mr-1" />
              {showUnreadOnly ? 'Tutti i messaggi' : 'Solo non letti'}
            </Button>
            <Button 
              variant={showArchived ? "secondary" : "ghost"} 
              size="sm" 
              className="text-xs flex-1"
              onClick={() => setShowArchived(!showArchived)}
            >
              <Archive className="h-3 w-3 mr-1" />
              {showArchived ? 'Nascondi archiviate' : 'Mostra archiviate'}
            </Button>
          </div>
        </div>

        <ScrollArea className="flex-1">
          {loading ? (
            <div className="divide-y">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="p-3 flex items-start gap-3 animate-pulse">
                  <div className="h-10 w-10 rounded-full bg-muted shrink-0" />
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="h-3 bg-muted rounded w-1/2" />
                      <div className="h-3 bg-muted rounded w-12" />
                    </div>
                    <div className="h-3 bg-muted rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : conversationGroups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Nessuna conversazione</p>
            </div>
          ) : (
            <div className="divide-y">
              {conversationGroups.map((group) => {
                const conv = group.primary;
                const isSelected = group.members.some(m => m.id === selectedConversation?.id);
                const hasUnread = group.totalUnread > 0;
                return (
                <div
                  key={group.key}
                  className={`p-3 cursor-pointer transition-colors border-l-2 ${
                    isSelected ? 'bg-muted border-l-primary' : hasUnread ? 'border-l-primary/60 hover:bg-muted/50' : 'border-l-transparent hover:bg-muted/50'
                  }`}
                  onClick={() => handleSelectConversation(conv)}
                >
                  <div className="flex items-start gap-3">
                    <div className="relative shrink-0">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback>
                          {conv.contact_name?.[0] || conv.contact_number.slice(-2)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="absolute -bottom-0.5 -right-0.5 bg-background rounded-full p-0.5 shadow-sm">
                        <ChannelIcon channel={conv.channel} className="h-3.5 w-3.5" />
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className={`truncate ${hasUnread ? 'font-semibold' : 'font-medium'}`}>
                            {conv.contact_name || conv.contact_number}
                          </span>
                          {hasUnread && (
                            <Badge className="bg-primary h-5 min-w-5 px-1 flex items-center justify-center rounded-full shrink-0">
                              {group.totalUnread}
                            </Badge>
                          )}
                          {conv.status === 'archived' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                              Archiviata
                            </span>
                          )}
                          {conv.crm_customer_type && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${
                              conv.crm_customer_type === 'b2c' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : conv.crm_customer_type === 'b2b' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                            }`}>
                              {CRM_ENTITY_LABEL[conv.crm_customer_type]}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0" title={format(new Date(conv.last_message_at), 'dd/MM/yyyy HH:mm:ss')}>
                          {getRelativeTime(conv.last_message_at)}
                        </span>
                      </div>
                      <div className="mt-1">
                        <p className="text-sm text-muted-foreground truncate min-w-0" title={conv.last_message}>{conv.last_message && conv.last_message.length > 42 ? `${conv.last_message.slice(0, 42)}…` : conv.last_message}</p>
                      </div>
                      {group.channels.length > 1 && (
                        <div className="flex items-center gap-1 mt-1">
                          {group.channels.map(ch => (
                            <span
                              key={ch}
                              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                            >
                              <ChannelIcon channel={ch} className="h-3 w-3" />
                              {CHANNEL_LABEL[ch]}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      <div className={`flex-1 flex flex-col ${!mobileShowDetail ? 'hidden md:flex' : 'flex'}`}>
        {selectedConversation ? (
          <>
            <div className="p-4 border-b flex items-center justify-between bg-background">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" className="md:hidden h-8 w-8 mr-1" onClick={handleMobileBack}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Avatar>
                  <AvatarFallback>
                    {selectedConversation.contact_name?.[0] || selectedConversation.contact_number.slice(-2)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">
                      {selectedConversation.contact_name || selectedConversation.contact_number}
                    </h3>
                    {selectedConversation.crm_customer_type && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        selectedConversation.crm_customer_type === 'b2c' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : selectedConversation.crm_customer_type === 'b2b' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                      }`}>
                        {CRM_ENTITY_LABEL[selectedConversation.crm_customer_type]}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ChannelIcon channel={selectedConversation.channel} className="h-4 w-4" />
                    {selectedConversation.contact_number}
                    {(selectedConversation.channel === 'instagram' || selectedConversation.channel === 'messenger') && selectedConversation.from_number && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" title={selectedConversation.channel === 'instagram' ? 'Account ricevente' : 'Pagina ricevente'}>
                        {selectedConversation.channel === 'instagram' ? 'Account ' : 'Pagina '}{senderLabel(selectedConversation.from_number)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {is24hWindowExpired(selectedConversation) && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                          <Timer className="h-3 w-3 mr-1" />
                          24h scadute
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{selectedConversation.channel === 'whatsapp'
                          ? 'Finestra 24h scaduta. Richiesto template per messaggiare.'
                          : 'Finestra 24h scaduta. Attendi un nuovo messaggio del contatto per rispondere.'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleExportGDPR} disabled={exporting}>
                      <Download className="h-4 w-4 mr-2" />
                      Export GDPR
                    </DropdownMenuItem>
                    {selectedConversation.status === 'archived' ? (
                      <DropdownMenuItem onClick={handleUnarchiveConversation}>
                        <ArchiveRestore className="h-4 w-4 mr-2" />
                        Ripristina conversazione
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={handleArchiveConversation}>
                        <Archive className="h-4 w-4 mr-2" />
                        Archivia conversazione
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleDeleteConversation} className="text-red-600">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Elimina conversazione
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <ScrollArea className="flex-1 p-4">
              <div className="space-y-3">
                {messages.map((msg, index) => {
                  const msgDay = msg.created_at.split('T')[0];
                  const prevDay = index > 0 ? messages[index - 1]!.created_at.split('T')[0]! : null;
                  const showSeparator = prevDay !== msgDay;
                  return (
                  <div key={msg.id}>
                  {showSeparator && (
                    <div className="flex items-center gap-3 my-4">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full font-medium select-none">
                        {getDateLabel(msg.created_at)}
                      </span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                  )}
                  <div
                    className={`flex group ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                    onMouseEnter={() => setHoveredMessageId(msg.id)}
                    onMouseLeave={() => { setHoveredMessageId(null); setShowQuickEmoji(null); }}
                  >
                    {msg.direction === 'outbound' && hoveredMessageId === msg.id && (
                      <div className="flex items-center gap-1 mr-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0"
                                onClick={() => handleReplyToMessage(msg)}
                              >
                                <Reply className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent><p>Rispondi</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <div className="relative">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 w-7 p-0"
                                  onClick={() => setShowQuickEmoji(showQuickEmoji === msg.id ? null : msg.id)}
                                >
                                  <Smile className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent><p>Reagisci</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {showQuickEmoji === msg.id && (
                            <div className="absolute bottom-8 right-0 bg-background border rounded-lg shadow-lg p-1 flex gap-1 z-50">
                              {['👍', '❤️', '😂', '😮', '😢', '👎'].map(emoji => (
                                <button
                                  key={emoji}
                                  className="text-lg hover:bg-muted p-1 rounded"
                                  onClick={() => handleQuickReaction(msg.id, emoji)}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {msg.status === 'failed' && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 w-7 p-0 text-orange-500 hover:text-orange-600"
                                  onClick={() => handleRetryMessage(msg.id)}
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent><p>Riprova</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                                onClick={() => handleDeleteMessage(msg.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent><p>Elimina</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2.5 shadow-sm ${
                        msg.direction === 'outbound'
                          ? 'bg-primary text-primary-foreground rounded-br-md'
                          : 'bg-muted rounded-bl-md'
                      }`}
                    >
                      {msg.reply_to_message && (
                        <div className={`mb-2 p-2 rounded-lg text-xs border-l-2 ${
                          msg.direction === 'outbound' 
                            ? 'bg-primary-foreground/10 border-primary-foreground/50' 
                            : 'bg-background/50 border-muted-foreground/50'
                        }`}>
                          <span className={`font-medium ${msg.direction === 'outbound' ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                            {msg.reply_to_message.direction === 'inbound' ? 'Loro' : 'Tu'}:
                          </span>
                          <p className={`truncate ${msg.direction === 'outbound' ? 'text-primary-foreground/80' : 'text-foreground/80'}`}>
                            {msg.reply_to_message.content.substring(0, 80)}{msg.reply_to_message.content.length > 80 ? '...' : ''}
                          </p>
                        </div>
                      )}
                      <MessageMedia mediaUrl={msg.media_url} mediaType={msg.media_type} />
                      {msg.content && <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>}
                      <div className={`flex items-center gap-1.5 mt-1 ${
                        msg.direction === 'outbound' ? 'justify-end' : 'justify-start'
                      }`}>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`text-[11px] cursor-help ${
                                msg.direction === 'outbound' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                              }`}>
                                {format(new Date(msg.created_at), 'dd/MM/yyyy HH:mm:ss')}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs space-y-1">
                              <p>Creato: {format(new Date(msg.created_at), 'dd/MM/yyyy HH:mm:ss')}</p>
                              {msg.sent_at && <p>Inviato: {format(new Date(msg.sent_at), 'dd/MM/yyyy HH:mm:ss')}</p>}
                              {msg.delivered_at && <p>Consegnato: {format(new Date(msg.delivered_at), 'dd/MM/yyyy HH:mm:ss')}</p>}
                              {msg.read_at && <p>Letto: {format(new Date(msg.read_at), 'dd/MM/yyyy HH:mm:ss')}</p>}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {msg.direction === 'outbound' && getStatusIcon(msg.status)}
                      </div>
                      {msg.error_message && (
                        <p className="text-xs text-red-300 mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {msg.error_message}
                        </p>
                      )}
                      {msg.reactions && msg.reactions.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {msg.reactions.map((reaction) => (
                            <button
                              key={reaction.emoji}
                              onClick={() => handleRemoveReaction(msg.id, reaction.emoji)}
                              disabled={!selectedConversation.can_reply}
                              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs 
                                ${msg.direction === 'outbound' 
                                  ? 'bg-primary-foreground/20 hover:bg-primary-foreground/30' 
                                  : 'bg-background hover:bg-background/80 border'
                                } transition-colors ${selectedConversation.can_reply ? 'cursor-pointer' : 'cursor-default'}`}
                            >
                              <span>{reaction.emoji}</span>
                              <span className={msg.direction === 'outbound' ? 'text-primary-foreground/80' : 'text-muted-foreground'}>
                                {reaction.count}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedConversation.can_reply && msg.direction === 'inbound' && hoveredMessageId === msg.id && (
                      <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0"
                                onClick={() => handleReplyToMessage(msg)}
                              >
                                <Reply className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent><p>Rispondi</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <div className="relative">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 w-7 p-0"
                                  onClick={() => setShowQuickEmoji(showQuickEmoji === msg.id ? null : msg.id)}
                                >
                                  <Smile className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent><p>Reagisci</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {showQuickEmoji === msg.id && (
                            <div className="absolute bottom-8 left-0 bg-background border rounded-lg shadow-lg p-1 flex gap-1 z-50">
                              {['👍', '❤️', '😂', '😮', '😢', '👎'].map(emoji => (
                                <button
                                  key={emoji}
                                  className="text-lg hover:bg-muted p-1 rounded"
                                  onClick={() => handleQuickReaction(msg.id, emoji)}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            <div className="p-4 border-t bg-background">
              {!selectedConversation.can_reply ? (
                <div
                  className="flex items-start gap-2 rounded-lg border border-muted bg-muted/40 p-3 text-sm text-muted-foreground"
                  role="status"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Storico in sola lettura: il sender {CHANNEL_LABEL[selectedConversation.channel]} non è più collegato.
                  </span>
                </div>
              ) : (
                <>
              {replyToMessage && (
                <div className="mb-2 p-2 bg-muted rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <Reply className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <span className="text-xs text-muted-foreground">Rispondi a:</span>
                      <p className="text-sm truncate">{replyToMessage.content}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setReplyToMessage(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              {is24hWindowExpired(selectedConversation) && (selectedConversation.channel === 'instagram' || selectedConversation.channel === 'messenger') ? (
                <div className="mb-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3 dark:bg-yellow-900/20 dark:border-yellow-800">
                  <div className="flex items-start gap-2 text-sm text-yellow-700 dark:text-yellow-400">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>Finestra 24h scaduta su {CHANNEL_LABEL[selectedConversation.channel]}. Per ricontattare il contatto fuori finestra seleziona un message tag Meta ammesso. Usa i tag solo per i casi consentiti dalle policy Meta.</span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm text-gray-700 dark:text-gray-300">Message tag</Label>
                      <Select value={selectedMetaTag} onValueChange={(v) => setSelectedMetaTag(v as MetaMessageTag)}>
                        <SelectTrigger className="bg-white dark:bg-gray-900">
                          <SelectValue placeholder="Seleziona un tag..." />
                        </SelectTrigger>
                        <SelectContent>
                          {META_ALLOWED_TAGS_BY_CHANNEL[selectedConversation.channel].map(tag => (
                            <SelectItem key={tag} value={tag}>{META_TAG_LABEL[tag]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <form onSubmit={(e) => { e.preventDefault(); sendMetaTagMessage(); }} className="flex gap-2">
                      <Input
                        placeholder="Scrivi un messaggio..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        disabled={sending || !selectedMetaTag}
                        className="flex-1 bg-white dark:bg-gray-900"
                      />
                      <Button type="submit" disabled={sending || !selectedMetaTag || !newMessage.trim()}>
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </form>
                  </div>
                </div>
              ) : is24hWindowExpired(selectedConversation) && selectedConversation.channel === 'whatsapp' ? (
                <div className="mb-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-sm text-yellow-700">
                    <AlertTriangle className="h-4 w-4" />
                    <span>Finestra 24h scaduta. Seleziona un template approvato per inviare un messaggio.</span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm text-gray-700">Template</Label>
                      <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Seleziona template..." />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedTemplateId && templates.find(t => t.id === selectedTemplateId)?.body_text && (
                      <div className="p-2 bg-white rounded border text-sm text-gray-600">
                        {templates.find(t => t.id === selectedTemplateId)?.body_text}
                      </div>
                    )}
                    {Object.keys(templateVariables).length > 0 && (() => {
                      const tpl = templates.find(t => t.id === selectedTemplateId);
                      const varMapping = tpl?.variables && !Array.isArray(tpl.variables) ? tpl.variables : null;
                      return (
                        <div className="space-y-2">
                          {Object.keys(templateVariables).map(key => {
                            const mappedVar = varMapping?.[key];
                            const isAutoFilled = !!templateVariables[key] && !!mappedVar;
                            return (
                              <div key={key}>
                                <div className="flex items-center gap-1.5 mb-1">
                                  <Label className="text-xs text-gray-600">{`{{${key}}}`}</Label>
                                  {mappedVar && (
                                    <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${isAutoFilled ? 'bg-green-100 text-green-700' : ''}`}>
                                      {mappedVar.replace(/_/g, ' ')}
                                      {isAutoFilled && ' ✓'}
                                    </Badge>
                                  )}
                                </div>
                                <Input
                                  value={templateVariables[key]}
                                  onChange={(e) => setTemplateVariables(prev => ({ ...prev, [key]: e.target.value }))}
                                  placeholder={mappedVar ? `Auto: ${mappedVar.replace(/_/g, ' ')}` : `Valore per ${key}`}
                                  className="bg-white"
                                />
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                    <Button 
                      onClick={sendTemplateMessage} 
                      disabled={!selectedTemplateId || sendingTemplate}
                      className="w-full"
                    >
                      {sendingTemplate ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Invia Template
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {attachments.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          {attachments.length} {attachments.length === 1 ? 'allegato in coda' : 'allegati in coda'}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={clearAttachments}
                          disabled={sending}
                          className="h-6 px-2 text-xs text-gray-500 dark:text-gray-400"
                        >
                          Rimuovi tutti
                        </Button>
                      </div>
                      {attachments.map((att) => (
                        <div key={att.id} className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2">
                          {att.previewUrl ? (
                            <img src={att.previewUrl} alt={att.file.name} className="h-12 w-12 rounded object-cover" />
                          ) : (
                            <div className="flex h-12 w-12 items-center justify-center rounded bg-gray-200 dark:bg-gray-700">
                              <Paperclip className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{att.file.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{(att.file.size / (1024 * 1024)).toFixed(2)} MB</p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAttachment(att.id)}
                            disabled={sending}
                            className="h-8 w-8 p-0"
                            title="Rimuovi allegato"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <form onSubmit={(e) => { e.preventDefault(); replyToMessage ? sendMessageWithReply() : sendMessage(); }} className="flex gap-2 relative">
                    <div className="relative">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        className="h-10 w-10 p-0"
                      >
                        <Smile className="h-5 w-5" />
                      </Button>
                      {showEmojiPicker && (
                        <div className="absolute bottom-12 left-0 z-50">
                          <Picker data={data} onEmojiSelect={handleEmojiSelect} theme="light" locale="it" />
                        </div>
                      )}
                    </div>
                    {selectedConversation?.channel === 'whatsapp' && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowTemplateShortcut(true)}
                        className="h-10 w-10 p-0"
                        title="Invia Template"
                      >
                        <FileText className="h-5 w-5" />
                      </Button>
                    )}
                    {(selectedConversation?.channel === 'instagram' || selectedConversation?.channel === 'messenger') && (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept={META_ATTACH_ACCEPT}
                          onChange={handleFileSelected}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={sending}
                          className="h-10 w-10 p-0"
                          title="Allega file"
                        >
                          <Paperclip className="h-5 w-5" />
                        </Button>
                      </>
                    )}
                    <Input
                      placeholder="Scrivi un messaggio..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={sending}
                      className="flex-1"
                    />
                    <Button type="submit" disabled={sending || (!newMessage.trim() && attachments.length === 0)}>
                      {sending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </form>
                </div>
              )}
                </>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Seleziona una conversazione</p>
              <p className="text-sm">Clicca su una chat per visualizzare i messaggi</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        className="mt-4"
                        onClick={openNewConversation}
                        disabled={activeChannel === 'all'}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Nuova Conversazione
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {activeChannel === 'all' && (
                    <TooltipContent>
                      Seleziona prima un canale (SMS, WhatsApp o Telegram) dalle schede a sinistra
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        )}
      </div>

      <NewSmsConversationDialog
        open={newConversationChannel === 'sms'}
        onClose={closeNewConversation}
        onSuccess={handleNewConversationSuccess}
      />
      <NewWhatsAppConversationDialog
        open={newConversationChannel === 'whatsapp'}
        onClose={closeNewConversation}
        onSuccess={handleNewConversationSuccess}
      />
      <NewTelegramConversationDialog
        open={newConversationChannel === 'telegram'}
        onClose={closeNewConversation}
        onSuccess={handleNewConversationSuccess}
      />

      {selectedConversation?.channel === 'whatsapp' && (
        <WhatsAppTemplateSendModal
          open={showTemplateShortcut}
          onClose={() => setShowTemplateShortcut(false)}
          conversationId={selectedConversation.id}
          contactPhone={selectedConversation.contact_number}
          contactName={selectedConversation.contact_name || selectedConversation.contact_number}
          onSuccess={() => {
            loadMessages(selectedConversation.id);
            loadConversations();
          }}
        />
      )}
    </div>
  );
}
